import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import Button from "../../../components/ui/Button";
import { WebPaths } from "../../../../../routes/data";
import { indHeroOne, indHeroThree } from "../../../../../images";
import { FaArrowRight } from "react-icons/fa6";
import {
  ShieldCheck,
  CheckCircle2,
  FileText,
  Clock,
  BadgeCheck,
  ChevronRight,
} from "lucide-react";
import { US, GB, CA, AU, EU } from "country-flag-icons/react/3x2";

const APPLICATION_STEPS = [
  { label: "Eligibility Check", done: true },
  { label: "Document Upload", done: true },
  { label: "AI Review", done: true },
  { label: "Expert Sign-off", active: true },
  { label: "Submission", done: false },
];

const Banner = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(3);

  return (
    <section className="bg-white-f9 overflow-hidden">
      <div className="relative isolate pt-14 lg:pt-24 pb-16 lg:pb-24">
        {/* Background blob */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80 pointer-events-none"
        >
          <div
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
            className="relative left-[calc(50%-11rem)] aspect-1155/678 w-144.5 -translate-x-1/2 rotate-30 bg-linear-to-tr from-blue-ea to-teal-00 opacity-10 sm:left-[calc(50%-30rem)] sm:w-288.75"
          />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-12 items-center">
            {/* ── Left Column ── */}
            <div className="lg:col-span-5 flex flex-col justify-center text-center lg:text-left">
              {/* Rating badge */}
              {/* <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center justify-center lg:justify-start gap-2 mb-6"
              >
                <div className="flex text-amber-fF">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <span className="text-dark-6b font-medium text-sm">
                  4.9/5 · 2,000+ approved applications
                </span>
              </motion.div>


              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex justify-center lg:justify-start mb-4"
              >
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-ea px-4 py-1.5 text-sm font-semibold text-blue-00 border border-blue-00/10">
                  <BadgeCheck className="w-3.5 h-3.5" />
                  Visa Services
                </span>
              </motion.div> */}

              {/* Heading */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-balance text-h1 font-light font-spartan tracking-tight text-black-1a"
              >
                Visa Applications, <br className="hidden lg:block" />
                Powered by{" "}
                <span className="inline-flex bg-teal-00 border-8 border-blue-ea! text-white px-3 pb-1 rounded-2xl rotate-2 hover:rotate-0 italic mx-1 shadow-md transition-transform duration-300">
                  Intelligence
                </span>
              </motion.h1>

              {/* Sub-copy */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-6 text-pretty text-dark-6b leading-relaxed mx-auto lg:mx-0 max-w-xl"
              >
                From document preparation to interview readiness, SeamlessVisa
                guides every step with AI precision and expert oversight — so
                your application tells the right story the first time.
              </motion.p>

              {/* Trust badges */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-10 flex flex-col gap-4 max-w-md mx-auto lg:mx-0 text-left"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-teal-00 shrink-0" />
                  <span className="text-black-1a font-medium">
                    AI-powered document review & error detection
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-teal-00 shrink-0" />
                  <span className="text-black-1a font-medium">
                    Expert immigration attorneys on every case
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-teal-00 shrink-0" />
                  <span className="text-black-1a font-medium">
                    Real-time status tracking from start to approval
                  </span>
                </div>
              </motion.div>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-12 flex flex-wrap items-center justify-center lg:justify-start gap-x-4 gap-y-4"
              >
                <Button
                  label="Check My Eligibility"
                  onClick={() => navigate(`${WebPaths.checkEligibility}/ds160`)}
                  variant="primary"
                  size="large"
                  endIcon={<FaArrowRight />}
                />
              </motion.div>
            </div>

            {/* ── Right Column: Bento Showcase ── */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="lg:col-span-7 mt-16 lg:mt-0 relative w-full"
            >
              <div className="grid grid-cols-2 gap-4 w-full lg:py-6">
                {/* Spotlight image card */}
                <div className="col-span-2 overflow-hidden rounded-4xl h-[260px] sm:h-[320px] relative group bg-white border border-transparent shadow-md transition-all duration-300 hover:border-blue-ea hover:shadow-xl hover:-translate-y-1">
                  <img
                    src={indHeroOne}
                    className="w-full h-full object-cover object-top transition-transform duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1.0)] group-hover:scale-[1.03]"
                    alt="Professional reviewing visa documents"
                  />
                  {/* Approval stamp */}
                  <div className="absolute top-5 right-5 bg-white/95 backdrop-blur-md px-4 py-2 rounded-full border border-grey-eb shadow-lg flex items-center gap-2">
                    <BadgeCheck className="w-4 h-4 text-teal-00" />
                    <span className="text-xs font-bold text-black-31 uppercase tracking-wide">
                      Visa Approved
                    </span>
                  </div>
                  {/* Success stat overlay */}
                  <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-grey-eb shadow-lg">
                    <p className="text-xs text-dark-6b font-medium">
                      Approval Rate
                    </p>
                    <p className="text-xl font-bold text-black-1a">98.4%</p>
                  </div>
                </div>

                {/* Interactive application tracker */}
                <div className="col-span-1 overflow-hidden rounded-3xl h-auto relative bg-white border border-grey-eb shadow-sm p-4 flex flex-col gap-2">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-blue-00" />
                    <span className="text-xs font-bold text-black-1a uppercase tracking-wide">
                      Application Status
                    </span>
                  </div>
                  {APPLICATION_STEPS.map((step, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveStep(i)}
                      className={`flex items-center gap-2 w-full text-left px-2.5 py-1.5 rounded-lg transition-all duration-200 ${
                        i === activeStep ? "bg-blue-ea" : "hover:bg-surface-alt"
                      }`}
                    >
                      <span
                        className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${
                          step.done
                            ? "bg-teal-00 text-white"
                            : i === activeStep
                              ? "bg-blue-00 text-white"
                              : "bg-grey-eb text-dark-6b"
                        }`}
                      >
                        {step.done ? "✓" : i + 1}
                      </span>
                      <span
                        className={`text-xs font-medium truncate ${
                          i === activeStep
                            ? "text-blue-00 font-semibold"
                            : step.done
                              ? "text-black-1a"
                              : "text-dark-6b"
                        }`}
                      >
                        {step.label}
                      </span>
                      {i === activeStep && (
                        <ChevronRight className="w-3 h-3 text-blue-00 ml-auto shrink-0" />
                      )}
                    </button>
                  ))}
                </div>

                {/* Supporting visual card */}
                <div className="col-span-1 overflow-hidden rounded-3xl relative group bg-white border border-transparent shadow-sm transition-all duration-300 hover:border-blue-ea hover:shadow-xl hover:-translate-y-1">
                  <img
                    src={indHeroThree}
                    className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1.0)] group-hover:scale-[1.03]"
                    alt="Expert visa consultation"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-black/30 to-transparent" />
                  <div className="absolute bottom-3 left-3 text-white">
                    <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
                      Expert Review
                    </p>
                    <p className="text-sm font-bold">24hr turnaround</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Countries marquee */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="w-full border-t border-grey-eb mt-20 lg:mt-24 pt-6 pb-6 bg-white/50 backdrop-blur-sm lg:bg-transparent"
        >
          <div className="flex items-center w-full">
            <div className="flex-none px-6 lg:pl-8 lg:pr-12 border-r border-grey-eb flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-blue-ea flex items-center justify-center">
                <span className="text-blue-00 text-lg">🌍</span>
              </div>
              <p className="hidden md:block text-sm font-bold text-black-1a tracking-wide uppercase">
                Supported Destinations
              </p>
            </div>
            <div className="flex-1 overflow-hidden pointer-events-none">
              <Marquee gradient={false} speed={40} className="overflow-hidden">
                <div className="flex items-center gap-12 pr-12">
                  {[
                    { Flag: US, name: "United States" },
                    { Flag: GB, name: "United Kingdom" },
                    { Flag: CA, name: "Canada" },
                    { Flag: AU, name: "Australia" },
                    { Flag: EU, name: "Schengen Area" },
                    { Flag: US, name: "United States" },
                    { Flag: GB, name: "United Kingdom" },
                  ].map(({ Flag, name }, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Flag
                        title={name}
                        className="w-6 h-4 rounded-sm shadow-sm"
                      />
                      <span className="text-sm font-bold text-black-31 uppercase tracking-wide">
                        {name}
                      </span>
                    </div>
                  ))}
                </div>
              </Marquee>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Banner;
